import axios from 'axios';
import type { Restaurant, MenuItem, ProcessingResponse, ChatSession } from '../types';

// Configure your Lambda function URLs here
const MAIN_API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://078kr22uq0.execute-api.us-west-1.amazonaws.com/Prod';
const CHAT_API_BASE_URL = import.meta.env.VITE_CHAT_API_BASE_URL || 'https://078kr22uq0.execute-api.us-west-1.amazonaws.com/Prod';

const api = axios.create({
  baseURL: MAIN_API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

const chatApiClient = axios.create({
  baseURL: CHAT_API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor for debugging
api.interceptors.request.use(
  (config) => {
    console.log('API Request:', config.method?.toUpperCase(), config.url, config.data);
    return config;
  },
  (error) => {
    console.error('API Request Error:', error);
    return Promise.reject(error);
  }
);

// Add response interceptor for debugging
api.interceptors.response.use(
  (response) => {
    console.log('API Response:', response.status, response.data);
    return response;
  },
  (error) => {
    console.error('API Response Error:', error.response?.data || error.message);
    return Promise.reject(error);
  }
);

export const restaurantApi = {
  // Get all restaurants
  getRestaurants: async (): Promise<Restaurant[]> => {
    // Return the known restaurants since listing endpoint doesn't exist yet
    console.log('Using known restaurant data');
    return [
      {
        restaurant_id: 'rest_0e6db7532020',
        name: 'Tony\'s Italian Kitchen',
        cuisine_type: 'Italian',
        description: 'Authentic Italian cuisine since 1952',
        location: {
          address: '123 Main St',
          city: 'San Francisco',
          state: 'CA',
          zip_code: '',
          country: 'US'
        },
        contact: {
          phone: '(555) 123-4567',
          email: 'info@tonysitalian.com',
          website: ''
        },
        metadata: {
          total_menu_items: 61,
          menu_last_updated: '2025-07-06T22:07:44.366Z'
        },
        created_at: '2025-07-06T19:55:30.488Z',
        updated_at: '2025-07-06T19:55:30.488Z'
      },
      {
        restaurant_id: 'rest_becf5d33015c',
        name: 'Mikes Grillhouse',
        cuisine_type: 'American',
        description: 'Great Food, Good Friends',
        location: {
          address: '3801 Pelandale Avenue',
          city: 'Modesto',
          state: 'CA',
          zip_code: '',
          country: 'US'
        },
        contact: {
          phone: '(209) 545-4500',
          email: 'info@mikesgrillhouse.com',
          website: ''
        },
        metadata: {
          total_menu_items: 73,
          menu_last_updated: '2025-07-07T17:55:13.027Z'
        },
        created_at: '2025-07-06T22:14:52.341Z',
        updated_at: '2025-07-06T22:14:52.341Z'
      }
    ];
  },

  // Get restaurant by ID
  getRestaurant: async (id: string): Promise<Restaurant> => {
    const response = await api.get(`/restaurants/${id}`);
    return response.data.restaurant;
  },

  // Create new restaurant
  createRestaurant: async (restaurant: Partial<Restaurant>): Promise<Restaurant> => {
    const response = await api.post('/restaurants', restaurant);
    return response.data.restaurant;
  },

  // Update restaurant
  updateRestaurant: async (id: string, restaurant: Partial<Restaurant>): Promise<Restaurant> => {
    const response = await api.put(`/restaurants/${id}`, restaurant);
    return response.data.restaurant;
  },

  // Delete restaurant
  deleteRestaurant: async (id: string): Promise<void> => {
    await api.delete(`/restaurants/${id}`);
  },
};

export const menuApi = {
  // Get menu items for a restaurant
  getMenuItems: async (restaurantId: string): Promise<MenuItem[]> => {
    try {
      const response = await api.get(`/restaurants/${restaurantId}/menu`);
      return response.data.menu_items || [];
    } catch (error) {
      console.warn('Menu API not available, returning empty menu:', error);
      return [];
    }
  },

  // Upload and process menu (PDF/Image)
  uploadMenu: async (
    restaurantId: string,
    file: File,
    menuType: 'pdf' | 'image'
  ): Promise<ProcessingResponse> => {
    const formData = new FormData();
    formData.append('restaurant_id', restaurantId);
    formData.append('menu_type', menuType);
    formData.append('file', file);

    const response = await api.post('/menu/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // Upload JSON menu
  uploadJsonMenu: async (
    restaurantId: string,
    menuData: any
  ): Promise<ProcessingResponse> => {
    const response = await api.post('/menu/upload', {
      restaurant_id: restaurantId,
      menu_type: 'json',
      menu_data: menuData,
    });
    return response.data;
  },

  // Update menu item
  updateMenuItem: async (
    restaurantId: string,
    itemId: string,
    item: Partial<MenuItem>
  ): Promise<MenuItem> => {
    const response = await api.put(`/restaurants/${restaurantId}/menu/${itemId}`, item);
    return response.data.menu_item;
  },

  // Delete menu item
  deleteMenuItem: async (restaurantId: string, itemId: string): Promise<void> => {
    await api.delete(`/restaurants/${restaurantId}/menu/${itemId}`);
  },
};

export const chatApi = {
  // Start new chat session
  startChat: async (
    restaurantId: string, 
    customerName?: string, 
    customerPreferences?: any
  ): Promise<{
    success: boolean;
    session: ChatSession;
    welcome_message: string;
    restaurant: {
      name: string;
      cuisine_type: string;
      description: string;
    };
  }> => {
    const response = await chatApiClient.post('/chat/start', { 
      restaurant_id: restaurantId,
      customer_name: customerName,
      customer_preferences: customerPreferences
    });
    return response.data;
  },

  // Send message to chat
  sendMessage: async (
    sessionId: string,
    message: string,
    customerContext?: any
  ): Promise<{
    success: boolean;
    session_id: string;
    response: string;
    message_count: number;
    timestamp: string;
  }> => {
    const response = await chatApiClient.post('/chat/message', {
      session_id: sessionId,
      message: message,
      customer_context: customerContext,
    });
    return response.data;
  },

  // Get chat history
  getChatHistory: async (sessionId: string): Promise<{
    success: boolean;
    session: ChatSession;
    messages: Array<{
      message_id: string;
      role: 'user' | 'assistant';
      content: string;
      timestamp: string;
      type?: string;
      context?: any;
    }>;
    context: any;
  }> => {
    const response = await chatApiClient.get(`/chat/history/${sessionId}`);
    return response.data;
  },

  // Get all sessions for a restaurant
  getRestaurantSessions: async (restaurantId: string): Promise<{
    success: boolean;
    restaurant_id: string;
    sessions: Array<{
      session_id: string;
      customer_name: string;
      created_at: string;
      updated_at: string;
      status: string;
      message_count: number;
      last_activity?: string;
    }>;
    total_sessions: number;
  }> => {
    const response = await chatApiClient.get(`/chat/sessions/${restaurantId}`);
    return response.data;
  },
};

// Error handling utility
export const handleApiError = (error: any): string => {
  if (error.response?.data?.error) {
    return error.response.data.error;
  }
  if (error.response?.data?.message) {
    return error.response.data.message;
  }
  if (error.message) {
    return error.message;
  }
  return 'An unexpected error occurred';
};

export default api; 